import { Component, DestroyRef, EventEmitter, inject, input, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import _ from 'lodash';
import { MessageService } from 'primeng/api';
import { Observable, catchError, concatMap, forkJoin, map, of } from 'rxjs';
import {
  InventoryTransaction,
  InventoryTransactionStatus,
  InventoryTransactionType,
} from '../../../../models/inventory/inventory-transaction';
import { InventoryTransactionNeededElement } from '../../../../models/inventory/inventory-transaction-needed-element';
import { LotItem } from '../../../../models/inventory/lot-item';
import { Invoice } from '../../../../models/invoice/invoice';
import { ItemMaster } from '../../../../models/item/item-master';
import { WorkStation } from '../../../../models/item/work-station';
import { Warehouse } from '../../../../models/warehouse.ts/warehouse';
import { LotItemProxyService } from '../../../../proxies/inventory/lot-item.proxy.service';
import { WarehouseProxyService } from '../../../../proxies/inventory/warehouse.proxy';
import { WorkStationProxyService } from '../../../../proxies/item/work-station.proxy';
import { InventoryTransactionService } from '../../../../services/base-actions/inventory-transaction.service';
import {
  InventoryTransactionManagerFactory,
  InventoryTransactionManagerKey,
} from '../../../../services/factory/inventory-transaction.factory';
import { EntityKeyByReference, EnumValues } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { Business, BusinessEntityType } from '../../../../models/finance/business';
import { InventorySearchCommand } from '../../../../models/inventory-summary';
import { EntityKeyByNumber } from '../../../../models/inventory/keys/entity-key';
import { BuildPathFromCode, LocationNode } from '../../../../models/location';
import { Roll } from '../../../../models/production/roll';
import { RollStatus } from '../../../../models/production/roll-search';
import { Translation } from '../../../../models/translation';
import { BusinessProxyService } from '../../../../proxies/finance/business.proxy.service';
import { RollProxyService } from '../../../../proxies/production/roll.proxy';
import { BarcodeScannerService } from '../../../../services/barcode-scanner.service';
import { EnumService } from '../../../../services/enum.service';
import { LocalHelperService } from '../../../../services/local-helper.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-inventory-transaction',
  templateUrl: './inventory-transaction.component.html',
  styleUrl: './inventory-transaction.component.scss',
})
export class InventoryTransactionComponent implements OnInit {
  @Input() canEdit = true;
  @Input()
  inventoryTransactionNeededElements: InventoryTransactionNeededElement[] = [];
  @Input() inventoryTransaction: InventoryTransaction | null = null;
  @Input() lotItems: LotItem[] = [];
  @Input() dischargingLotItems = false;
  @Input() type: InventoryTransactionManagerKey = 'warehouse-import';
  @Input() parentInitiator: EntityKeyByReference | null = null;
  @Input() isShipmentReadyToUnload = false;
  @Input() isConsignment = false;
  @Input() readonly = false;
  @Output() updateInventoryTransaction = new EventEmitter<InventoryTransactionNeededElement>();
  @Input() knittingOrderNumber = '';

  warehouses: Warehouse[] = [];
  workStations: WorkStation[] = [];
  providers: Business[] = [];
  localSavedLotItems: LotItem[] = [];
  receivedStatus: number = InventoryTransactionStatus.Received;
  inRouteStatus: number = InventoryTransactionStatus.InRoute;

  get allowTransferUnitActions(): boolean {
    return (
      this.inventoryTransaction?.status !== InventoryTransactionStatus.InRoute &&
      this.inventoryTransaction?.status !== InventoryTransactionStatus.Received
    );
  }

  get isAnyOperationInProgress(): boolean {
    return this.inventoryTransactionManager?.isOperationInProgress() ?? false;
  }

  inventoryTransactionManager?: InventoryTransactionService;

  lotItemLoadStatus: Record<string, boolean> = {};
  invoiceAndLots: Record<string, LotItem[]> = {};
  invoices: Invoice[] = [];
  warehouse: FormControl<Warehouse | null> = new FormControl<Warehouse | null>(null);
  workStation: FormControl<WorkStation | null> = new FormControl<WorkStation | null>(null);
  lotItemSearch: FormControl<LotItem | null> = new FormControl<LotItem | null>(null);

  lotItemFilterGroup: FormGroup = new FormGroup({
    filterByInvoice: new FormControl(false),
    filterByItemCode: new FormControl(false),
  });

  selectedInvoices: FormControl<Invoice[] | null> = new FormControl<Invoice[] | null>([]);
  selectedItemCodes: FormControl<ItemMaster[] | null> = new FormControl<ItemMaster[] | null>([]);
  searchBy: FormControl<string | null> = new FormControl<string | null>(null);

  warehouseOptions: Warehouse[] = [];
  workStationOptions: WorkStation[] = [];

  shipmentStatus = input<number | null>(null);
  //transactionStatus = input<number | null>(null);

  //#region Transfer Unit Variables
  rollSearch: FormControl<Roll | null> = new FormControl<Roll | null>(null);
  translations: Record<string, Translation> = {
    Unsaved: {
      en: 'Unsaved',
      es: 'Sin guardar',
    },
  };

  private readonly workStationProxyService = inject(WorkStationProxyService);
  private readonly warehouseProxyService = inject(WarehouseProxyService);
  private readonly lotItemProxyService = inject(LotItemProxyService);
  private readonly messageService = inject(MessageService);
  private readonly inventoryTransactionManagerFactory = inject(InventoryTransactionManagerFactory);
  private readonly barcodeScannerService = inject(BarcodeScannerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly enumService = inject(EnumService);
  private readonly rollProxyService = inject(RollProxyService);
  private readonly businessProxyService = inject(BusinessProxyService);
  private readonly translateService = inject(TranslateService);
  public readonly localHelperService = inject(LocalHelperService);

  inventoryTransactionStatus: EnumValues = this.enumService.getEnum('InventoryTransactionStatus', 'inventory').values;
  unitOfMeasure: EnumValues = this.enumService.getEnum('UnitOfMeasure', 'item').values;

  addRollToTransaction(roll: Roll, add: boolean) {
    if (add && roll?.status === RollStatus.Rejected) {
      GlobalMessageUtil.error(
        this.translateService.instant('Inventory_Transaction_Rejected_Roll_Title'),
        this.translateService.instant('Inventory_Transaction_Rejected_Roll_Message', { number: roll.number }),
      );
      return;
    }

    // Check if any operation is in progress to prevent concurrent operations
    if (this.inventoryTransactionManager?.isOperationInProgress()) {
      console.warn('Operation already in progress, please wait...');
      return;
    }

    const lotItemKey = {
      entityDefinitionCode: 2,
      number: roll.number!,
    };

    // For transfer units, ensure we have an inventory transaction ID
    let transactionId = this.inventoryTransaction?.id;
    if (this.type === 'transfer-unit-details' && !transactionId) {
      // For transfer units, we can use the single transaction or it will be created automatically
      transactionId = undefined; // Let the service handle creation
    }

    this.inventoryTransactionManager!.upsertLotItemKey(lotItemKey, add, transactionId, false).subscribe((results) => {
      if (results && add) {
        this.inventoryTransactionManager?.addRollToRecord(this.inventoryTransaction!.id!, roll);
      } else if (results && !add) {
        this.inventoryTransactionManager?.removeRollFromRecord(this.inventoryTransaction!.id!, roll);
      }
    });
  }

  ngOnInit(): void {
    this.selectedInvoices.valueChanges.subscribe((value) => {
      if (!_.isEmpty(value)) {
        const selectedItemCodes = this.selectedItemCodes.value ?? [];

        const selectedInvoiceIds = value!.map((invoice) => invoice.id);

        const filteredItemCodes = selectedItemCodes.filter((item) =>
          this.lotItems.some(
            (lotItem) =>
              lotItem.invoiceReference && selectedInvoiceIds.includes(lotItem.invoiceReference) && lotItem.itemCode === item.itemCode,
          ),
        );

        this.selectedItemCodes.setValue(filteredItemCodes, {
          emitEvent: false,
        });
      }
    });
    this.lotItemSearch.valueChanges.subscribe((value) => {
      if (value?.id) {
        this.lotItemSearch.setValue(null, { emitEvent: false });

        const element = this.inventoryTransactionNeededElements.find(
          (element) =>
            element.Item.itemCode === value.itemCode &&
            element.LotNumber === value.providerLotId &&
            element.Provider.id === value.manufacturerReference,
        );
        if (element) {
          this.lotItemProxyService.getLotItemById(value.id!).subscribe({
            next: (response) => {
              const lotItem = response.data;
              if (lotItem) {
                if (this.isLotItemKeyReceived(lotItem, this.inventoryTransaction!)) {
                  GlobalMessageUtil.warn(
                    this.translateService.instant('Lot_Item_Already_In_List_Title'),
                    this.translateService.instant('Lot_Item_Already_In_List_Message', { number: lotItem.number }),
                  );
                  return;
                }
                this.upsertLotItemKey(lotItem, this.inventoryTransaction!);
              }
            },
            error: (error) => {
              console.error('Error receiving lot item:', error);
            },
          });
        } else {
          GlobalMessageUtil.error(
            'No matching entry in requisition',
            'There is no matching entry in the requisition for the selected lot item. Please check the requisition details.',
          );
        }
      }
    });
    this.rollSearch.valueChanges.subscribe((value) => {
      if (this.readonly) {
        GlobalMessageUtil.info('Read-only mode', 'Cannot add rolls in read-only mode.');
      }
      if (value?.id) {
        if (value.status === RollStatus.Rejected) {
          GlobalMessageUtil.error(
            this.translateService.instant('Inventory_Transaction_Rejected_Roll_Title'),
            this.translateService.instant('Inventory_Transaction_Rejected_Roll_Message', { number: value.number }),
          );
          this.rollSearch.setValue(null, { emitEvent: false });
          return;
        }
        this.addRollToTransaction(value, true);
      }
    });

    forkJoin([
      this.workStationProxyService.getAll(),
      this.warehouseProxyService.getAllWarehouses(),
      this.businessProxyService.search({ businessTypes: [BusinessEntityType.Provider, BusinessEntityType.Client] }, 1, 200), // Assuming 5 is the code for providers
    ]).subscribe({
      next: ([results, warehousesResponse, providerResults]) => {
        let workStations = results.data;
        const warehouses = warehousesResponse.data?.map((w) => ({
          ...w,
          fieldToDisplay: '(' + w.code + ') ' + w.name,
        }));

        workStations = workStations!.map((w) => ({
          ...w,
          fieldToDisplay: w.name,
        }));

        let providers = providerResults.data?.items ?? [];
        providers = providers.map((p) => ({
          ...p,
          fieldToDisplay: '(' + p.code + ') ' + p.name,
        }));

        this.providers = providers;
        this.warehouses = warehouses!;
        this.workStations = workStations;

        this.warehouseOptions = warehouses!;
        this.workStationOptions = workStations;
      },
      error: (error) => {
        console.error('Error loading work stations or warehouses:', error);
      },
    });
    this.inventoryTransactionManager = this.inventoryTransactionManagerFactory.getManager(this.type);

    this.barcodeScannerService.barcode$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        concatMap((barcode) => {
          const activeTag = document.activeElement?.tagName.toLowerCase();
          if (activeTag === 'input' || activeTag === 'textarea') {
            return of(null);
          }

          const cleanBarcode = barcode.includes('#') ? barcode.split('#').pop() || '' : barcode;

          if (this.type === 'requisition-details') {
            return this.attemptToAddLotItemToRequisitionAsync(cleanBarcode);
          } else if (cleanBarcode.startsWith('R-')) {
            this.attemptToGetRollByQuery(cleanBarcode);
            return of(null);
          } else {
            this.attemptToReceiveLotItemByQuery(cleanBarcode);
            return of(null);
          }
        }),
      )
      .subscribe();

    if (
      !_.isNil(this.inventoryTransaction) &&
      this.localSavedLotItems?.length !== InventoryTransactionService.GetLotItemKeys(this.inventoryTransaction).length
    ) {
      if (this.type !== 'transfer-unit-details') {
        this.lotItemProxyService
          .getByLotNumbers(InventoryTransactionService.GetLotItemKeys(this.inventoryTransaction).map((lotItem) => lotItem.number!) ?? [])
          .subscribe({
            next: (response) => {
              const lotItems = response.data;
              if (lotItems) {
                this.localSavedLotItems = lotItems;
              }
            },
          });
      }
    }

    if (!this.canEdit || this.readonly) {
      this.lotItemSearch.disable();
    }
  }

  getLocalLotItems(inventoryTransactionNeededElement: InventoryTransactionNeededElement): LotItem[] {
    return this.localSavedLotItems.filter((lotItem) => {
      return (
        lotItem.itemCode === inventoryTransactionNeededElement.Item.itemCode &&
        lotItem.manufacturerReference === inventoryTransactionNeededElement.Provider.id &&
        lotItem.providerLotId === inventoryTransactionNeededElement.LotNumber
      );
    });
  }

  attemptToAddLotItemToRequisition(query: string): void {
    this.attemptToAddLotItemToRequisitionAsync(query).subscribe();
  }

  attemptToAddLotItemToRequisitionAsync(query: string): Observable<void> {
    const comingFrom = !_.isNil(this.comingFromLocationCode) ? BuildPathFromCode(this.comingFromLocationCode) : undefined;
    if (_.isNil(comingFrom)) {
      GlobalMessageUtil.warn('Error, No Location selected', 'Please select a location to search for lot items.');
      return of(undefined);
    }

    // Extract unique values to avoid duplicates and improve performance
    const manufacturerReferences = Array.from(
      new Set(this.inventoryTransactionNeededElements.map((element) => element.Provider.id!).filter((id) => id != null)),
    );

    const itemCodes = Array.from(
      new Set(this.inventoryTransactionNeededElements.map((element) => element.Item.itemCode).filter((code) => code != null)),
    );

    const manufacturerLotNumbers = Array.from(
      new Set(this.inventoryTransactionNeededElements.map((element) => element.LotNumber).filter((lotNumber) => lotNumber != null)),
    );

    const command: InventorySearchCommand = {
      manufacturerReferences: manufacturerReferences,
      locationPaths: [comingFrom],
      itemCodes: itemCodes,
      manufacturerLotNumbers: manufacturerLotNumbers,
      lotItemNumbers: !_.isEmpty(query) ? [query] : undefined,
      // manufacturerLabelNumbers: !_.isEmpty(query) ? [query] : undefined,
    };

    return this.lotItemProxyService.batchSearch(command).pipe(
      map((response) => {
        const lotItems: LotItem[] = response.data ?? [];
        const lotItem = lotItems.find((item) => item.number === query);

        if (lotItems.length > 1) {
          GlobalMessageUtil.warn(
            this.translateService.instant('Inventory_Transaction_Multiple_Lot_Items_Found_Title'),
            this.translateService.instant('Inventory_Transaction_Multiple_Lot_Items_Found_Message') +
              lotItems.map((item) => item.number).join(', '),
          );
        }

        if (lotItem) {
          this.lotItemSearch.setValue(lotItem);
        } else {
          GlobalMessageUtil.error('Lot item not found', 'No lot item found for the provided query: ' + query);
        }
      }),
      catchError((error) => {
        console.error('Error searching lot items:', error);
        return of(undefined);
      }),
    );
  }

  attemptToReceiveLotItemByQuery(query: string): void {
    //look through all the lot items using first hte lotItem.providerLotId and then the lotItemNumber
    // if found, receive it
    // if not found, show a message
    // if found, but already received, show a message

    if (!query) {
      return;
    }

    // If no inventory transaction exists, create a draft one first
    if (_.isNil(this.inventoryTransaction)) {
      // Only check for operations in progress when creating a new transaction to prevent multiple creation
      if (this.inventoryTransactionManager?.isOperationInProgress()) {
        console.warn('Transaction creation in progress, please wait...');
        return;
      }

      console.log('No inventory transaction found, creating draft transaction for scanning...');
      this.inventoryTransactionManager?.createLocalInventoryTransaction(InventoryTransactionType.Transfer);

      // After creating local transaction, get the draft transaction
      const draftTransaction = this.inventoryTransactionManager?.getOpenInventoryTransaction();
      if (!draftTransaction) {
        GlobalMessageUtil.error('Error', 'Unable to create inventory transaction for scanning.');
        return;
      }
    }

    const lotItems = this.lotItems.filter((lot) => lot.providerLabelNumber === query || lot.number === query);

    if (!lotItems || lotItems.length === 0) {
      GlobalMessageUtil.error('Lot item not found', 'No lot item found for the provided query: ' + query);
      return;
    }

    if (lotItems.length > 1) {
      GlobalMessageUtil.warn(
        this.translateService.instant('Inventory_Transaction_Multiple_Lot_Items_Found_Title'),
        this.translateService.instant('Inventory_Transaction_Multiple_Lot_Items_Found_Message') +
          lotItems.map((item) => item.number).join(', '),
      );
      return;
    }
    const lotItem = lotItems[0];
    const lotItemKey: EntityKeyByNumber = {
      entityDefinitionCode: 1,
      number: lotItem.number!,
    };

    if (InventoryTransactionService.GetLotItemKeys(this.inventoryTransaction!).some((itemKey) => _.isEqual(itemKey, lotItemKey))) {
      GlobalMessageUtil.error('Error', 'Lot item already received: ' + lotItem.providerLabelNumber + ' or ' + lotItem.number);
      return;
    } else {
      this.upsertLotItemKey(lotItem, this.inventoryTransaction!);
    }
  }

  get comingFromLocationCode(): string | undefined {
    return this.inventoryTransaction?.fromLocationCode;
  }

  get goingToLocationCode(): string | undefined {
    return this.inventoryTransaction?.toLocationCode;
  }

  getLocationByCode(locationCode: string): LocationNode | undefined {
    return this.inventoryTransactionManager?.getLocation(locationCode) ?? undefined;
  }

  getEntityKeyByReferenceFieldToDisplay(EntityKey: EntityKeyByReference | undefined): string {
    if (!EntityKey) {
      return '';
    }
    switch (EntityKey.entityDefinitionCode) {
      case 0:
        return this.warehouses.find((warehouse) => warehouse.id === EntityKey.reference)?.fieldToDisplay ?? '';
      case 8:
        return this.workStations.find((workStation) => workStation.id === EntityKey.reference)?.fieldToDisplay ?? '';
      case 5:
        return this.providers.find((provider) => provider.id === EntityKey.reference)?.fieldToDisplay ?? '';
      default:
        return 'Unknown';
    }
  }

  getItemProgress(inventoryTransactionNeededElement: InventoryTransactionNeededElement): string {
    const total = inventoryTransactionNeededElement.NeededQuantity;
    const completed = this.getLocalLotItems(inventoryTransactionNeededElement).reduce((acc, lotItem) => {
      return acc + (lotItem.netQuantity ?? 0);
    }, 0);
    if (completed >= 0) {
      inventoryTransactionNeededElement.LoadedQuantity = completed;
      this.updateInventoryTransaction.emit(inventoryTransactionNeededElement);
    }
    return `${completed.toLocaleString(undefined, { maximumFractionDigits: 2 })}/${total.toLocaleString()}`;
  }

  getRolls() {
    return this.inventoryTransactionManager?.getRollsByInventoryTransaction(this.inventoryTransaction!.id!) ?? [];
  }

  getTotalWeight(): number {
    return this.getRolls().reduce((acc, roll) => acc + (roll.netQuantity ?? 0), 0) ?? 0;
  }

  getTotalNetQuantity(lotItems: LotItem[], type: 'gross' | 'net'): number {
    if (type === 'gross') {
      return lotItems.reduce((acc, lotItem) => acc + (lotItem.originalGrossQuantity ?? 0), 0);
    } else {
      return lotItems.reduce((acc, lotItem) => acc + (lotItem.originalNetQuantity ?? 0), 0);
    }
  }

  copyTransactionId(fullId: string): void {
    navigator.clipboard.writeText(fullId).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('Inventory_Transaction_Search_Copied'),
        detail: this.translateService.instant('Inventory_Transaction_Copied_To_Clipboard'),
        life: 2000,
      });
    });
  }

  getStatusLabel() {
    if (_.isNil(this.inventoryTransaction?.id)) {
      return this.translations['Unsaved'][this.translateService.currentLang as keyof Translation];
    }

    return this.inventoryTransactionStatus[this.inventoryTransaction.status];
  }

  getRecordsKeysWithLots(): string[] {
    return Object.keys(this.invoiceAndLots).filter((key) => this.invoiceAndLots[key].length > 0);
  }

  getLotsProgress(key: string): number {
    const lots = this.invoiceAndLots[key];
    if (!lots || lots.length === 0) {
      return 0;
    }
    const total = lots.length;
    const completed = lots.filter((lot) => lot.netQuantity > 0).length;
    return (completed / total) * 100;
  }

  getLotItems() {
    const selectedInvoices = this.selectedInvoices.value ?? [];
    const selectedItems = this.selectedItemCodes.value ?? [];

    const invoiceReferences = selectedInvoices.map((invoice) => invoice.id);
    const itemCodes = selectedItems.map((item) => item.itemCode);
    const searchBy = this.searchBy.value;

    const filterByInvoice = invoiceReferences.length > 0;
    const filterByItemCode = itemCodes.length > 0;
    const filterBySearch = !_.isNil(searchBy);

    const lotItems = this.lotItems.filter((lotItem) => {
      const invoiceMatch = !filterByInvoice || invoiceReferences.includes(lotItem.invoiceReference);
      const itemMatch = !filterByItemCode || itemCodes.includes(lotItem.itemCode);
      const searchMatch = filterBySearch ? lotItem.providerLabelNumber?.includes(searchBy) || lotItem.number?.includes(searchBy) : true;
      return invoiceMatch && itemMatch && searchMatch;
    });

    if (this.getStatusLabel() === 'Completed') {
      return lotItems.filter((lotItem) => this.isLotItemKeyReceived(lotItem, this.inventoryTransaction!));
    } else {
      return lotItems;
    }
  }

  getDistictInvoiceOptions(): Invoice[] {
    //get unique invoices from lot items
    const invoices: Invoice[] =
      this.lotItems.map((lotItem) => lotItem.invoice).filter((invoice): invoice is Invoice => invoice !== undefined) ?? [];
    return _.uniqBy(invoices, (invoice) => invoice.id);
  }

  getItemOptions(): ItemMaster[] {
    //filter by invoices if  selectedInvoiceIds is not empty
    const selectedInvoiceIds = this.selectedInvoices.value?.map((invoice) => invoice.id);
    //get unique item codes from lot items that their invoiceReference is in selectedInvoiceIds, if selectedInvoiceIds is empty, return all item codes
    const items: ItemMaster[] =
      this.lotItems
        .filter((lotItem) => {
          if (selectedInvoiceIds && selectedInvoiceIds.length > 0) {
            return selectedInvoiceIds.includes(lotItem.invoiceReference);
          }
          return true;
        })
        .map((lotItem) => lotItem.item)
        .filter((item): item is ItemMaster => item != null) ?? [];

    return _.uniqBy(items, (item) => item.itemCode);
  }

  getInvoiceById(id: string): Invoice | undefined {
    return this.invoices.find((invoice) => invoice.id === id);
  }

  getLotsById(id: string): LotItem[] {
    return this.invoiceAndLots[id] || [];
  }

  receiveLotItem(lotItem: LotItem): void {
    // Logic to handle receiving a lot item
    console.log('Received Lot Item:', lotItem);
  }

  getLotItemLabel(lotItem: LotItem) {
    this.lotItemProxyService.getLotItemLabel(lotItem.id!).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        // Open the image in a new tab
        window.open(url, '_blank');
        // Optionally, you can revoke the object URL after a delay:
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      },
      error: (error) => {
        console.error('Error downloading lot item label:', error);
      },
    });
  }

  //#region Lot Item actons
  public fetchLotItems = (query: string): Observable<LotItem[]> => {
    const comingFrom = !_.isNil(this.comingFromLocationCode) ? BuildPathFromCode(this.comingFromLocationCode) : undefined;
    if (_.isNil(comingFrom)) {
      GlobalMessageUtil.warn('Error, No Location selected', 'Please select a location to search for lot items.');
      return of([]);
    }

    // Extract unique values to avoid duplicates and improve performance
    const manufacturerReferences = Array.from(
      new Set(
        this.inventoryTransactionNeededElements.map((element) => element.Provider.id!).filter((id) => id != null), // Remove null/undefined values
      ),
    );

    const itemCodes = Array.from(
      new Set(
        this.inventoryTransactionNeededElements.map((element) => element.Item.itemCode).filter((code) => code != null), // Remove null/undefined values
      ),
    );

    const manufacturerLotNumbers = Array.from(
      new Set(
        this.inventoryTransactionNeededElements.map((element) => element.LotNumber).filter((lotNumber) => lotNumber != null), // Remove null/undefined values
      ),
    );
    const command: InventorySearchCommand = {
      manufacturerReferences: manufacturerReferences,
      locationPaths: [comingFrom],
      itemCodes: itemCodes,
      manufacturerLotNumbers: manufacturerLotNumbers,
      lotItemNumbers: !_.isEmpty(query) ? [query] : undefined,
    };

    return this.lotItemProxyService.batchSearch(command).pipe(
      map((response) => {
        const lotItems: LotItem[] = response.data ?? [];

        if (lotItems.length === 0) {
          GlobalMessageUtil.warn('Not lot items found', 'No lot items found matching query, manufacturer and manufacturer lot number.');
          return [];
        }

        return lotItems
          .filter((lotItem) => {
            return !this.inventoryTransaction?.lines.map((line) => line.lotItemKey).some((key) => lotItem.number === key?.number);
          })
          .map((lotItem) => ({
            ...lotItem,
            fieldToDisplay: `(${lotItem.number}) - ${lotItem.itemCode} - ${lotItem.netQuantity} `,
          }));
      }),
      catchError((error) => {
        console.error('Error searching lot items:', error);
        return of([] as LotItem[]);
      }),
    );
  };

  //#endregion

  upsertLotItemKey(lotItem: LotItem, inventoryTransaction: InventoryTransaction) {
    const lotItemKey: EntityKeyByNumber = {
      entityDefinitionCode: 1,
      number: lotItem.number!,
    };
    this.inventoryTransactionManager
      ?.upsertLotItemKey(lotItemKey, !this.isLotItemKeyReceived(lotItem, inventoryTransaction), inventoryTransaction.id)
      .subscribe((success) => {
        if (success) {
          if (_.isNil(inventoryTransaction.id)) {
            inventoryTransaction =
              this.inventoryTransactionManager!.inventoryTransactions().find((transaction) =>
                InventoryTransactionService.GetLotItemKeys(transaction).some((key) => _.isEqual(key, lotItemKey)),
              ) ?? inventoryTransaction;
          }
          if (this.isLotItemKeyReceived(lotItem, inventoryTransaction)) {
            this.localSavedLotItems.push(lotItem);
          } else {
            this.localSavedLotItems = this.localSavedLotItems.filter((item) => item.id !== lotItem.id);
          }
        }
      });
  }

  isLotItemKeyReceived(lotItem: LotItem, inventoryTransaction: InventoryTransaction): boolean {
    const lotItemKey: EntityKeyByNumber = {
      entityDefinitionCode: 1,
      number: lotItem.number!,
    };
    return InventoryTransactionService.GetLotItemKeys(inventoryTransaction).some((localLotItemKey) =>
      _.isEqual(localLotItemKey, lotItemKey),
    );
  }

  isLotItemLoading(lotItem: LotItem): boolean {
    const lotItemKey: EntityKeyByNumber = {
      entityDefinitionCode: 1,
      number: lotItem.number!,
    };
    return this.inventoryTransactionManager?.isLotItemKeyLoading(lotItemKey) || false;
  }

  getProgress(): number {
    const completedValues = [InventoryTransactionStatus.Received, InventoryTransactionStatus.InRoute];
    if (_.isNil(this.inventoryTransaction)) {
      return 0;
    }

    if (completedValues.includes(this.inventoryTransaction!.status!)) {
      return 100;
    }
    const totalScanned = InventoryTransactionService.GetLotItemKeys(this.inventoryTransaction).length;
    const total = this.lotItems.length;
    if (totalScanned === 0 || total === 0) {
      return 0;
    }
    return Math.floor((totalScanned / total) * 100);
  }

  saveInventoryTransaction() {
    if (this.inventoryTransaction) {
      if (_.isNil(this.inventoryTransaction.id)) {
        this.inventoryTransactionManager?.createInventoryTransaction().subscribe((response) => {
          console.log('Inventory transaction created:', response);
        });
      } else {
        this.inventoryTransactionManager?.saveInventoryTransaction(this.inventoryTransaction.id!);
      }
    }
  }

  //#region Transfer Unit
  attemptToGetRollByQuery(query: string): void {
    if (this.readonly) {
      GlobalMessageUtil.warn('Read-only mode', 'Cannot add rolls in read-only mode.');
      return;
    }
    this.rollProxyService.getByRollNumber(query, true, this.knittingOrderNumber).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const roll = response.data;
          this.rollSearch.setValue(roll);
        }
      },
      error: () => {
        //attempt to find it in existing rolls of transaction, if not found then we show this error.
        //if found, we display warning that roll is already in the transaction.
        const existingRoll = this.inventoryTransactionManager
          ?.getRollsByInventoryTransaction(this.inventoryTransaction!.id!)
          .find((r) => (r.number ?? '').toLowerCase() === query.toLowerCase());
        if (existingRoll) {
          GlobalMessageUtil.warn('Roll already exists', `Roll with number ${existingRoll.number} is already in the inventory transaction.`);
        }
      },
    });
  }
  //#endregion

  // The debounce window stays at 2s via [searchOptions] in the template; what is new is that a slower
  // earlier lookup can no longer land on top of a newer one.
  public fetchRolls = (query: string): Observable<Roll[]> => {
    if (!query) return of([]);

    const rollNumber = !query.startsWith('R-') && !query.startsWith('r-') ? 'R-' + query : query;

    return this.rollProxyService
      .getByRollNumber(rollNumber, true, this.knittingOrderNumber)
      .pipe(map((response) => (response.data ? [response.data] : [])));
  };
}
